import { logger } from '../logger';

type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
  failedAt?: number;
};

type AnyAsyncFn = (...args: any[]) => Promise<any>;

/**
 * SWR + LKG 폴백을 가진 인메모리 캐시 옵션.
 *
 * - 캐시 키에는 요청 헤더/쿠키/사용자 컨텍스트가 포함되지 않습니다. **모든 사용자에게
 *   동일한 응답이 안전한 auth-agnostic API에만 사용**하세요. upstream 응답이 인증·locale·
 *   AB 등에 따라 분기된다면 cacheKey가 그 차원을 반영하거나 personalization 헤더 발견 시
 *   null을 반환해 캐시를 우회해야 합니다.
 * - timeout 발동 시 외부 fetcher의 underlying 요청은 abort되지 않습니다.
 *   abort가 필요하면 fetcher 측에서 자체 timeout 또는 AbortSignal을 보장하세요.
 * - 백그라운드 refresh 실패 후 failureCooldownMs 동안은 추가 백그라운드 refetch를
 *   트리거하지 않고 캐시를 그대로 반환합니다 (SSR 폭주 방지).
 * - cacheKey가 null을 반환하면 해당 호출은 캐시를 우회합니다.
 * - lkgDurationMs를 지난 entry는 다음 refresh 시점에 정리되고, store 크기가 maxEntries를
 *   초과하면 가장 오래 사용되지 않은(LRU) entry부터 제거됩니다.
 */
export interface SwrCacheOptions<TFn extends AnyAsyncFn> {
  name: string;
  fetcher: TFn;
  cacheKey: (...args: Parameters<TFn>) => string | null;
  freshDurationMs?: number;
  staleDurationMs?: number;
  lkgDurationMs?: number;
  failureCooldownMs?: number;
  timeoutMs?: number;
  maxEntries?: number;
  shouldFallback?: (error: unknown) => boolean;
  now?: () => number;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const DEFAULT_FRESH_MS = HOUR_MS;
const DEFAULT_STALE_MS = DAY_MS;
const DEFAULT_LKG_MS = 7 * DAY_MS;
const DEFAULT_TIMEOUT_MS = 1500;
const DEFAULT_FAILURE_COOLDOWN_MS = 30 * 1000;
const DEFAULT_MAX_ENTRIES = 1000;

const isFourXX = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

const defaultShouldFallback = (err: unknown): boolean => !isFourXX(err);

const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const id = setTimeout(
      () => reject(new Error(`${label} timeout after ${timeoutMs}ms`)),
      timeoutMs,
    );
    promise.then(
      (value) => {
        clearTimeout(id);
        resolve(value);
      },
      (err) => {
        clearTimeout(id);
        reject(err);
      },
    );
  });

export function swrCache<TFn extends AnyAsyncFn>({
  name,
  fetcher,
  cacheKey,
  freshDurationMs = DEFAULT_FRESH_MS,
  staleDurationMs = DEFAULT_STALE_MS,
  lkgDurationMs = DEFAULT_LKG_MS,
  failureCooldownMs = DEFAULT_FAILURE_COOLDOWN_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  maxEntries = DEFAULT_MAX_ENTRIES,
  shouldFallback = defaultShouldFallback,
  now = Date.now,
}: SwrCacheOptions<TFn>): TFn {
  type TArgs = Parameters<TFn>;
  type TResult = Awaited<ReturnType<TFn>>;

  const store = new Map<string, CacheEntry<TResult>>();
  const pending = new Map<string, Promise<TResult>>();

  const evict = () => {
    const cutoff = now() - lkgDurationMs;
    store.forEach((entry, k) => {
      if (entry.fetchedAt < cutoff) {
        store.delete(k);
      }
    });
    while (store.size > maxEntries) {
      const oldestKey = store.keys().next().value;
      if (oldestKey === undefined) break;
      store.delete(oldestKey);
    }
  };

  const refresh = (key: string, args: TArgs): Promise<TResult> => {
    const inflight = pending.get(key);
    if (inflight) return inflight;

    const promise = withTimeout(fetcher(...args), timeoutMs, `[swrCache:${name}]`)
      .then((data: TResult) => {
        store.delete(key);
        store.set(key, { data, fetchedAt: now() });
        evict();
        return data;
      })
      .catch((err: unknown) => {
        // shouldFallback을 통과한 에러(5xx/네트워크/타임아웃)만 cooldown 대상으로 기록한다.
        // 4xx 같은 폴백 제외 응답은 failedAt을 기록하지 않아 cooldown에 영향 주지 않는다 —
        // 다음 요청도 그대로 throw 시맨틱을 유지해야 함 (요청 결함은 폴백 시 버그 은닉).
        if (shouldFallback(err)) {
          const current = store.get(key);
          if (current) {
            store.set(key, { ...current, failedAt: now() });
          }
        }
        throw err;
      })
      .finally(() => {
        pending.delete(key);
      });

    pending.set(key, promise);
    return promise;
  };

  const wrapped = async (...args: TArgs): Promise<TResult> => {
    const key = cacheKey(...args);
    if (key === null) {
      return fetcher(...args);
    }

    const entry = store.get(key);

    if (!entry) {
      return refresh(key, args);
    }

    store.delete(key);
    store.set(key, entry);

    const age = now() - entry.fetchedAt;

    if (age <= freshDurationMs) {
      return entry.data;
    }

    if (age <= staleDurationMs) {
      const recentFailure =
        entry.failedAt !== undefined && now() - entry.failedAt < failureCooldownMs;
      if (!recentFailure) {
        void refresh(key, args).catch((err) => {
          logger.warn(`[swrCache:${name}] background refresh failed: ${errorMessage(err)}`);
        });
      }
      return entry.data;
    }

    if (age <= lkgDurationMs) {
      // 최근 실패 후 cooldown 안이면 백엔드를 또 두드리지 않고 즉시 LKG 반환 — 회복 시간 보호.
      const recentFailure =
        entry.failedAt !== undefined && now() - entry.failedAt < failureCooldownMs;
      if (recentFailure) {
        return entry.data;
      }
      try {
        return await refresh(key, args);
      } catch (err) {
        if (shouldFallback(err)) {
          logger.error(
            `[swrCache:${name}] fetch failed, returning LKG (age=${Math.round(
              age / 1000,
            )}s): ${errorMessage(err)}`,
          );
          return entry.data;
        }
        throw err;
      }
    }

    return refresh(key, args);
  };

  return wrapped as unknown as TFn;
}

/**
 * stale-if-error 패턴의 인메모리 캐시 (LKG-only 모드).
 *
 * 평상시는 매 호출마다 fetcher를 동기 실행해 최신 응답을 반환합니다. 백엔드가
 * 5xx/네트워크 에러/타임아웃을 반환하면 마지막 성공 응답으로 폴백하고 (4xx 제외),
 * 폴백 데이터의 age는 lkgDurationMs(기본 7일)까지 허용됩니다.
 *
 * 백엔드 부하 절감 목적이 아닌 **장애 복원력** 목적입니다. 평상시 응답 신선도와
 * cross-user 안전성을 보장하면서 단기 장애에 대한 fallback만 제공.
 *
 * 단, 폴백 시 마지막 성공 응답이 모든 사용자에게 노출되므로 **auth-agnostic 공개
 * 응답 API에만 적합**합니다. personalization이 도입되면 LKG 폴백 자체를 재검토해야
 * 합니다.
 */
export function lkgCache<TFn extends AnyAsyncFn>(
  options: Omit<SwrCacheOptions<TFn>, 'freshDurationMs' | 'staleDurationMs'>,
): TFn {
  // age는 항상 0 이상이므로 -1로 두면 fresh/stale 분기를 건너뛰고 항상 동기 refresh를 시도한다.
  return swrCache({
    ...options,
    freshDurationMs: -1,
    staleDurationMs: -1,
  });
}
